import { Link } from "react-router-dom";
import { Instagram, Facebook, Share2 } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative">
      {/* Torn Paper Edge */}
      <div className="relative h-12 overflow-hidden bg-background">
        <svg
          className="absolute bottom-0 left-0 w-full"
          viewBox="0 0 1200 40"
          preserveAspectRatio="none"
          style={{ height: "40px" }}
        >
          <path
            d="M0,40 L0,12 Q15,8 30,14 Q45,20 55,10 Q65,2 80,8 Q95,16 110,11 Q125,6 140,13 Q155,18 170,9 Q185,3 200,10 Q215,17 230,12 Q245,5 260,11 Q275,19 290,13 Q305,4 320,9 Q335,16 350,11 Q365,3 380,8 Q395,15 410,10 Q425,4 440,12 Q455,18 470,9 Q485,2 500,7 Q515,14 530,10 Q545,5 560,13 Q575,19 590,11 Q605,3 620,9 Q635,17 650,12 Q665,5 680,10 Q695,16 710,8 Q725,2 740,11 Q755,18 770,13 Q785,6 800,9 Q815,14 830,7 Q845,2 860,10 Q875,17 890,12 Q905,5 920,8 Q935,13 950,10 Q965,6 980,14 Q995,19 1010,11 Q1025,4 1040,9 Q1055,15 1070,10 Q1085,3 1100,8 Q1115,16 1130,12 Q1145,7 1160,13 Q1175,18 1190,10 L1200,8 L1200,40 Z"
            fill="hsl(160 65% 25%)"
          />
        </svg>
      </div>

      {/* Footer Body */}
      <div className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold mb-4">
                Rebecca Ryell
              </h3>
              <p className="text-primary-foreground text-sm leading-relaxed">
                Building resilience, not itineraries. Expert medical advice and adventure
                coaching for parents who want to show their children the real world.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-display text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/#hero"
                    className="text-primary-foreground hover:text-primary-foreground transition-colors text-sm"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#about"
                    className="text-primary-foreground hover:text-primary-foreground transition-colors text-sm"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#services"
                    className="text-primary-foreground hover:text-primary-foreground transition-colors text-sm"
                  >
                    Coaching
                  </Link>
                </li>
                <li>
                  <Link
                    to="/field-notes"
                    className="text-primary-foreground hover:text-primary-foreground transition-colors text-sm"
                  >
                    Field Notes
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact & Social */}
            <div>
              <h4 className="font-display text-lg font-bold mb-4">Get in Touch</h4>
              <p className="text-primary-foreground text-sm mb-2">
                Ready to start your family's adventure?
              </p>
              <Link
                to="/#services"
                className="text-primary-foreground hover:text-primary-foreground/80 transition-colors text-sm font-semibold underline"
              >
                Book a Discovery Call →
              </Link>

              {/* Social Icons */}
              <p className="font-mono text-xs uppercase tracking-widest mt-6 mb-3 text-primary-foreground">
                Follow the adventure
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground hover:text-accent transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={24} />
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground hover:text-accent transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={24} fill="currentColor" strokeWidth={0} />
                </a>
                <button
                  onClick={async () => {
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: "Rebecca Ryell",
                          url: window.location.href,
                        });
                      } catch {}
                    } else {
                      await navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="text-primary-foreground hover:text-accent transition-colors"
                  aria-label="Share this page"
                >
                  <Share2 size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-primary-foreground/30 my-8" />

          {/* Disclaimers - small print footnotes */}
          <div className="space-y-3 leading-relaxed">
            <p className="font-mono text-[10px] md:text-xs">
              <strong className="text-primary-foreground">Medical Disclaimer:</strong> Information
              provided is for educational purposes and does not constitute a medical diagnosis or
              doctor-patient relationship. Always consult with a qualified healthcare provider for
              medical advice specific to your situation.
            </p>
            <p className="font-mono text-[10px] md:text-xs">
              <strong className="text-primary-foreground">Home Exchange Disclosure:</strong> I work
              for Home Exchange because I love it, but opinions here are my own. Any recommendations
              reflect my genuine experience and belief in the service.
            </p>
          </div>

          {/* Legal Links + Copyright */}
          <div className="mt-8 pt-4 border-t border-primary-foreground/20 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link
                to="/privacy"
                className="font-mono text-[10px] text-primary-foreground hover:text-primary-foreground transition-colors"
              >
                Privacy &amp; Cookies
              </Link>
              <Link
                to="/terms"
                className="font-mono text-[10px] text-primary-foreground hover:text-primary-foreground transition-colors"
              >
                Terms &amp; Conditions
              </Link>
            </div>
            <p className="font-mono text-[10px] text-primary-foreground">
              © {currentYear} Rebecca Ryell Travel. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;